import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeftRight, ShieldCheck } from "lucide-react";

import { tr, L } from "../i18n";
const TAX_RATE = 0.25 * 1.055;
const ALLOWANCES = L([
  { v: 0, l: "None / already used" },
  { v: 1000, l: "€1,000 (single)" },
  { v: 2000, l: "€2,000 (joint)" },
]);
const TERMS = [3, 6, 9, 12, 18, 24, 36, 60];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cur, n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(n);

export default function CurrencyBuffer() {
  const [gbp, setGbp] = useState("50000");
  const [rate, setRate] = useState("4.00");
  const [term, setTerm] = useState(12);
  const [buffer, setBuffer] = useState(10);
  const [allowance, setAllowance] = useState(0);
  const [fx, setFx] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/fx/gbp-eur`).then((r) => setFx(r.data)).catch(() => setError(true));
  }, []);

  const amount = parseFloat(String(gbp).replace(/[,\s£]/g, ""));
  const rateVal = parseFloat(String(rate).replace(",", "."));
  const ok = fx && !isNaN(amount) && amount > 0 && !isNaN(rateVal) && rateVal >= 0;
  const gbpInterest = ok ? amount * (rateVal / 100) * (term / 12) : 0;
  const gbpTotal = ok ? amount + gbpInterest : 0;
  const eurValue = ok ? gbpTotal * fx.rate : 0;
  const taxableEur = Math.max(gbpInterest * (fx?.rate || 0) - allowance, 0);
  const tax = ok ? taxableEur * TAX_RATE : 0;
  const netEur = eurValue - tax;
  const safeValue = netEur * (1 - buffer / 100);

  return (
    <div data-testid="fx-converter" className="mt-6 reveal rounded-3xl bg-white ring-1 ring-[#0b1f3a]/10 p-7 md:p-8 shadow-xl shadow-[#0b1f3a]/5 grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2">
        <div className="flex items-center gap-2 text-[#c2a15a] text-[12px] uppercase tracking-widest font-medium">
          <ArrowLeftRight size={15} /> {tr("Pound savings in euros")}
        </div>
        <h3 className="font-display text-[22px] text-[#0b1f3a] mt-2">{tr("Exchange-rate check")}</h3>
        <label htmlFor="fx-gbp" className="block text-[13px] font-medium text-[#0b1f3a]/70 mt-5 mb-2">{tr("Savings balance (£)")}</label>
        <input id="fx-gbp" data-testid="fx-gbp-input" inputMode="decimal" value={gbp}
          onChange={(e) => setGbp(e.target.value)} className="rh-input" />
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div>
            <label htmlFor="fx-rate" className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-2">{tr("Interest rate p.a. (%)")}</label>
            <input id="fx-rate" data-testid="fx-rate-input" inputMode="decimal" value={rate}
              onChange={(e) => setRate(e.target.value)} className="rh-input" />
          </div>
          <div>
            <label htmlFor="fx-term" className="block text-[13px] font-medium text-[#0b1f3a]/70 mb-2">{tr("Term (months)")}</label>
            <select id="fx-term" data-testid="fx-term-select" value={term}
              onChange={(e) => setTerm(Number(e.target.value))} className="rh-input">
              {TERMS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <label htmlFor="fx-allowance" className="block text-[13px] font-medium text-[#0b1f3a]/70 mt-5 mb-2">{tr("Saver's allowance (Sparerpauschbetrag)")}</label>
        <select id="fx-allowance" data-testid="fx-allowance-select" value={allowance}
          onChange={(e) => setAllowance(Number(e.target.value))} className="rh-input">
          {ALLOWANCES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
        </select>
        <div className="flex justify-between text-[13px] font-medium text-[#0b1f3a]/70 mt-5 mb-2">
          <label htmlFor="fx-buffer">{tr("Safety margin for currency fluctuations")}</label>
          <span data-testid="fx-buffer-value" className="text-[#0b1f3a]">{buffer} %</span>
        </div>
        <input id="fx-buffer" data-testid="fx-buffer-slider" type="range" min="0" max="20" step="1" value={buffer}
          onChange={(e) => setBuffer(Number(e.target.value))} className="w-full accent-[#c2a15a]" />
      </div>

      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4 content-start">
        <Result testid="fx-gbp-interest" label={tr("Interest earned (£)")} value={ok ? fmt("GBP", gbpInterest) : "—"} />
        <Result testid="fx-gbp-total" label={tr("Total at maturity (£)")} value={ok ? fmt("GBP", gbpTotal) : "—"} />
        <Result testid="fx-eur-value" label={tr("Gross payout in € at today's rate")} value={ok ? fmt("EUR", eurValue) : "—"} />
        <Result testid="fx-tax" label={tr("Tax 25% + 5.5% solidarity surcharge")} value={ok ? `− ${fmt("EUR", tax)}` : "—"} />
        <Result testid="fx-eur-net" label={tr("Net payout in € after tax")} value={ok ? fmt("EUR", netEur) : "—"} />
        <Result testid="fx-eur-buffered" label={tr("Net payout after {v0} % margin", { v0: buffer })} value={ok ? fmt("EUR", safeValue) : "—"} gold />
        <p data-testid="fx-rate-info" className="sm:col-span-2 flex items-start gap-2 text-[12.5px] text-[#0b1f3a]/55 leading-relaxed">
          <ShieldCheck size={15} className="shrink-0 mt-0.5 text-[#c2a15a]" />
          {error
            ? tr("The exchange rate is currently unavailable. Please try again later.")
            : fx
            ? tr("£1 = €{v0} · {v1}, as of {v2}. German withholding tax (26.375% incl. solidarity surcharge, as of 2026) is applied to the interest only; church tax and currency gains are not included. The margin shows a conservative net payout should sterling weaken by maturity. Indicative only, not tax advice.", { v0: fx.rate.toFixed(4), v1: fx.source, v2: fx.date })
            : tr("Loading today's exchange rate…")}
        </p>
      </div>
    </div>
  );
}

const Result = ({ label, value, gold, testid }) => (
  <div className="rounded-2xl bg-[#0b1f3a] p-5">
    <p className="text-[12px] uppercase tracking-widest text-white/50">{label}</p>
    <p data-testid={testid} className={`font-display text-[30px] leading-none mt-2 ${gold ? "text-[#c2a15a]" : "text-white"}`}>{value}</p>
  </div>
);
